import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, ShieldCheck, CheckCircle2, IndianRupee } from "lucide-react";

export default function Payment() {
  const { bookingId } = useParams();
  const nav = useNavigate();
  const [intent, setIntent] = useState(null);
  const [booking, setBooking] = useState(null);
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/payments/${bookingId}/intent`),
      api.get(`/bookings/${bookingId}`),
    ]).then(([a, b]) => {
      setIntent(a.data); setBooking(b.data);
      if (b.data.payment_status === "paid") setDone(true);
    });
  }, [bookingId]);

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied"));
  };

  const pay = async () => {
    setPaying(true);
    try {
      // Simulate UPI processing time
      await new Promise((r) => setTimeout(r, 1600));
      const { data } = await api.post(`/bookings/${bookingId}/pay`);
      setBooking(data);
      setDone(true);
      toast.success("Payment successful. Booking confirmed!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPaying(false);
    }
  };

  if (!intent || !booking) return <div className="py-16 text-center text-sm text-muted-foreground">Loading payment...</div>;

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&data=${encodeURIComponent(intent.upi_url)}`;

  if (done) {
    return (
      <div className="mx-auto max-w-lg py-8 text-center" data-testid="payment-success">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-brand-orange text-white">
          <CheckCircle2 size={28} />
        </div>
        <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight">Booking confirmed!</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Your UPI payment of ₹{booking.amount} was successful. The provider has been notified.
        </p>
        <div className="mt-6 rounded-2xl border border-foreground/10 bg-white p-5 text-left">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Booking</div>
          <div className="mt-1 font-heading text-lg font-bold">{booking.service?.title}</div>
          <div className="mt-2 text-sm text-muted-foreground">
            {new Date(booking.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
          </div>
          <div className="mt-2 text-xs">
            <span className="text-muted-foreground">Txn ID:</span> <span className="font-mono">{booking.payment_txn_id}</span>
          </div>
        </div>
        <div className="mt-6 flex justify-center gap-3">
          <Button onClick={() => nav("/bookings")} className="rounded-full bg-brand-orange text-white hover:bg-brand-orange/90" data-testid="view-bookings-btn">
            View my bookings
          </Button>
          <Button onClick={() => nav("/browse")} variant="outline" className="rounded-full">Book more</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="payment-page">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-widest text-brand-orange">Step 2 of 2 · Pay by UPI</div>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Complete your payment</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-foreground/10 bg-white p-6 text-center">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Scan with any UPI app</div>
          <div className="mt-4 inline-block rounded-2xl border-4 border-brand-orange p-2">
            <img src={qrSrc} alt="UPI QR" className="h-64 w-64 rounded-xl" data-testid="upi-qr" />
          </div>
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck size={12} className="text-brand-orange" /> Secured via UPI · No card details stored
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-3xl border border-foreground/10 bg-white p-6">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Amount to pay</div>
            <div className="mt-1 flex items-center gap-1 font-heading text-4xl font-extrabold tracking-tight">
              <IndianRupee size={28} className="text-brand-orange" />
              {intent.amount}
            </div>

            <div className="mt-5 space-y-3">
              <button
                onClick={() => copy(intent.upi_id)}
                className="flex w-full items-center justify-between rounded-2xl border p-3 text-left transition-transform hover:-translate-y-0.5"
                data-testid="copy-upi-id"
              >
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">UPI ID</div>
                  <div className="font-mono text-sm font-bold">{intent.upi_id}</div>
                </div>
                <Copy size={16} />
              </button>
              <button
                onClick={() => copy(intent.reference)}
                className="flex w-full items-center justify-between rounded-2xl border p-3 text-left transition-transform hover:-translate-y-0.5"
                data-testid="copy-reference"
              >
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Reference</div>
                  <div className="font-mono text-sm font-bold">{intent.reference}</div>
                </div>
                <Copy size={16} />
              </button>
            </div>
          </div>

          <div className="rounded-3xl border border-brand-orange/30 bg-brand-orange/5 p-6">
            <h3 className="font-heading text-lg font-bold">Already paid?</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Once we receive the UPI confirmation, your booking is auto-confirmed. Tap below to verify now.
            </p>
            <Button
              onClick={pay}
              disabled={paying}
              className="mt-4 h-12 w-full rounded-full bg-brand-orange text-white hover:bg-brand-orange/90"
              data-testid="i-have-paid-btn"
            >
              {paying ? "Verifying payment..." : "I have paid — confirm booking"}
            </Button>
          </div>

          <Button onClick={() => nav(-1)} variant="ghost" className="w-full rounded-full text-muted-foreground">
            Cancel and go back
          </Button>
        </div>
      </div>
    </div>
  );
}
